import { moveItemInArray } from '@angular/cdk/drag-drop';
import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  Injector,
  linkedSignal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxAmountForm,
  OnyxCustomAddress,
  OnyxHttpErrorResponse,
  OnyxRoute,
  OnyxRouteSummary,
} from '@onyx/angular';
import {
  FormHelper,
  ONYX_INPUT_DEBOUNCE_TIME,
  OnyxFormMode,
  OnyxLanguagePipe,
  OnyxScheduleCompliance,
  OnyxToastService,
} from '@onyx/angular';
import { chain, isEqual, isString, sumBy, uniq } from 'lodash';
import { DateTime } from 'luxon';
import type { Observable } from 'rxjs';
import {
  combineLatestWith,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  forkJoin,
  isObservable,
  map,
  of,
  ReplaySubject,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
  zip,
} from 'rxjs';
import type { PartialDeep } from 'type-fest';
import { validate as validateUuid } from 'uuid';
import { AuthStore } from '../../../../auth/common/stores/auth.store';
import { DocumentsModalFormComponent } from '../../../../common/components/forms/documents-form/documents-modal-form/documents-modal-form.component';
import { ParametersFormComponent } from '../../../../common/components/forms/parameters-form/parameters-form.component';
import { SCHEDULE_COMPLIANCE_DELAY_RISK_THRESHOLD } from '../../../../common/constants/common/schedule-compliance-delay-risk-threshold';
import { VALIDATION_ERROR_CODES } from '../../../../common/constants/common/validation-error-codes';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../common/helpers/common.helper';
import { NavigationHelper } from '../../../../common/helpers/navigation.helper';
import { RouteHelper } from '../../../../common/helpers/route.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type {
  CalculateRouteRequest,
  RouteConfig,
} from '../../../../common/interfaces/address/calculate-route-request';
import type { BusinessHours } from '../../../../common/interfaces/common/business-hours';
import type { FleetValidationByType } from '../../../../common/interfaces/validation/fleet-validation';
import { JoinPipe } from '../../../../common/pipes/join.pipe';
import { AddressService } from '../../../../common/services/address.service';
import { AmountService } from '../../../../common/services/amount.service';
import { CacheService } from '../../../../common/services/cache.service';
import { PreferencesService } from '../../../../common/services/preferences.service';
import { RouteService } from '../../../../common/services/route.service';
import type { Contractor } from '../../../management-panel/contractors/common/interfaces/contractor';
import { CompanyDictionaryCode } from '../../../management-panel/dictionaries/common/enums/company-dictionary-code';
import { CompanyDictionariesService } from '../../../management-panel/dictionaries/common/services/company-dictionaries.service';
import { OrderFormRouteConfigModalComponent } from '../../order-form/order-form-route/order-form-route-config-modal/order-form-route-config-modal.component';
import { OrderCheckpointFormComponent } from '../../order-form/order-points-form/order-checkpoint-form/order-checkpoint-form.component';
import type {
  OrderPointForm,
  OrderPointFormGroup,
} from '../../order-form/order-points-form/order-point-form/order-point-form.component';
import { OrderTransitFormComponent } from '../../order-form/order-points-form/order-transit-form/order-transit-form.component';
import type { OrderUnloadingPointForm } from '../../order-form/order-points-form/order-unloading-point-form/order-unloading-point-form.component';
import { OrderUnloadingPointFormComponent } from '../../order-form/order-points-form/order-unloading-point-form/order-unloading-point-form.component';
import { LOADING_METERS_DIMENSIONS } from '../constants/loading-meters-dimensions';
import { LTL_ORDER_POINT_TYPES } from '../constants/ltl-order-point-types';
import { ORDER_FORM_GOOD_COLORS } from '../constants/order-form-good-colors';
import { OrderDispatch } from '../enums/order-dispatch';
import { OrderFormRestorePointType } from '../enums/order-form-cancel-action-type';
import { OrderPointCategory } from '../enums/order-point-category';
import type { OrderPointTypeByCategory } from '../enums/order-point-type';
import { OrderPointType } from '../enums/order-point-type';
import { OrderRateType } from '../enums/order-rate-type';
import { OrderTransitPointTimeWindowType } from '../enums/order-transit-point-time-window-type';
import { OrdersQueryParamKey } from '../enums/orders-query-param-key';
import { OrdersRoute } from '../enums/orders-route';
import { OrdersStorageKey } from '../enums/orders-storage-key';
import type {
  OrderForm,
  OrderFormDto,
  OrderFormGroup,
  OrderLoadingPointForm,
} from '../helpers/order-form.helper';
import { OrderFormHelper } from '../helpers/order-form.helper';
import { OrderHelper } from '../helpers/order.helper';
import type { Order, OrderReference } from '../interfaces/order';
import type { OrderFormGood } from '../interfaces/order-form-good';
import type { OrderFormPoint } from '../interfaces/order-form-point';
import type { OrderFormPrice } from '../interfaces/order-form-price';
import { OrdersService } from './orders.service';

@Injectable()
export class OrderFormService {
  private readonly authStore = inject(AuthStore);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly amountService = inject(AmountService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly preferencesService = inject(PreferencesService);
  private readonly toastService = inject(OnyxToastService);
  private readonly translateService = inject(TranslateService);
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly addressService = inject(AddressService);
  private readonly ordersService = inject(OrdersService);
  private readonly orderFormHelper = inject(OrderFormHelper);
  private readonly cacheService = inject(CacheService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly injector = inject(Injector);
  private readonly routeService = inject(RouteService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly orderHelper = inject(OrderHelper);
  private readonly returnToOrigin = NavigationHelper.getReturnToOrigin(
    this.router,
    {
      mode: () => this.mode(),
      uuid: () => this.uuid(),
      listUrl: OrdersRoute.ORDERS_LIST,
      cardUrl: OrdersRoute.ORDER_CARD,
      openModal: (uuid) => this.orderHelper.openModal(uuid),
    },
  );

  public readonly goodTypes$ = this.companyDictionariesService.reload$.pipe(
    startWith(undefined),
    switchMap(() =>
      this.companyDictionariesService.listDictionary(
        CompanyDictionaryCode.GOOD_TYPE,
      ),
    ),
    shareReplay(1),
    takeUntilDestroyed(this.destroyRef),
  );
  public readonly units$ = this.companyDictionariesService.reload$.pipe(
    startWith(undefined),
    switchMap(() =>
      this.companyDictionariesService.listDictionary(
        CompanyDictionaryCode.UNIT,
      ),
    ),
    shareReplay(1),
    takeUntilDestroyed(this.destroyRef),
  );

  private readonly I18N = 'orders.orderForm';

  public form = this.buildForm();
  public mode = signal(OnyxFormMode.ADD);
  public order = signal<Order | null>(null);
  public fleetValidation = signal<FleetValidationByType | null>(null);
  public initialState = signal<PartialDeep<OrderForm> | null>(null);

  public isDraft = computed(() =>
    this.order() ? OrderHelper.isDraft(this.order()!) : false,
  );
  public canDispatch = computed(
    () => this.mode() === OnyxFormMode.ADD || this.isDraft(),
  );

  public validation = computed(() =>
    this.fleetValidation()
      ? OrderFormHelper.getValidation(
          this.fleetValidation()!,
          this.vehicleTypes(),
        )
      : null,
  );

  public valueChanges$ = this.form.valueChanges.pipe(
    map(() => this.form.getRawValue()),
    distinctUntilChanged((previous, current) => isEqual(previous, current)),
    shareReplay(1),
    takeUntilDestroyed(this.destroyRef),
  );
  public value = toSignal(this.valueChanges$, {
    initialValue: this.form.getRawValue(),
  });
  public dispatch = toSignal(
    this.valueChanges$.pipe(map((form) => form.dispatch)),
    { initialValue: this.form.controls.dispatch.getRawValue() },
  );
  public vehicleTypes = toSignal(
    this.valueChanges$.pipe(map((form) => form.basicInformation.vehicleTypes)),
    { initialValue: [] },
  );
  public dischargeTypes = toSignal(
    this.valueChanges$.pipe(
      map((form) => form.basicInformation.dischargeTypes),
    ),
    { initialValue: [] },
  );
  public adrClasses = toSignal(
    this.valueChanges$.pipe(map((form) => form.parameters.adrClasses)),
    { initialValue: null },
  );
  public isDedicated = toSignal(
    this.valueChanges$.pipe(map((form) => form.parameters.isDedicated!)),
    { initialValue: true },
  );
  public hasEcmr = toSignal(
    this.valueChanges$.pipe(map((form) => form.parameters.hasEcmr!)),
    { initialValue: false },
  );

  public points = computed<OrderFormPoint[]>(() => {
    const formGoods = this.getGoods();
    const typeCounter = new Map<string, number>();

    const pointsForm = this.form.controls.points;
    const points = pointsForm.controls.map((form, index) => {
      const value = form.getRawValue() as OrderPointForm;
      const { category, type, uuid } = value;

      const typeIndex = typeCounter.get(type) ?? 0;
      typeCounter.set(type, typeIndex + 1);

      const goods = formGoods.filter(
        ({ good, pointIndex }) =>
          (pointIndex < index && good.type) || pointIndex === index,
      );

      const point = this.order()?.points.find((point) => point.uuid === uuid);
      return {
        form: form as OrderPointFormGroup,
        value,
        status: point?.status ?? null,
        timestamps: point?.timestamps ?? null,
        estimations: point?.estimations ?? null,
        index,
        isFirst: index === 0,
        isLast: index === pointsForm.length - 1,
        category,
        type,
        typeIndex,
        goods,
      };
    });

    return points;
  });

  public firstLoadingPoint = computed(
    () =>
      this.points().find((point) => point.type === OrderPointType.LOADING) ??
      null,
  );
  public lastUnloadingPoint = computed(
    () =>
      this.points().findLast(
        (point) => point.type === OrderPointType.UNLOADING,
      ) ?? null,
  );

  public hasInvalidAddress = computed(() =>
    this.value()
      .points.filter((point): point is OrderPointForm => point != null)
      .flatMap((point) =>
        'address' in point
          ? [point.address]
          : [point.startAddress, point.endAddress],
      )
      .some((address) => !address?.coordinates),
  );

  public routeConfig = signal<RouteConfig | null>(null);

  public showRoutePlanSkeleton = computed(
    () => this.isRoutePlan() && !this.activeRoute(),
  );
  public routes = computed(() => RouteHelper.getRouteProposals(this.routes_()));
  public activeRouteCompliance = computed(() => {
    this.value();
    if (!this.activeRoute()) return null;
    return this.validatePointsRoute({ showErrors: false });
  });

  private sender$_ = new ReplaySubject<Contractor | null>(1);
  public get sender$() {
    return this.sender$_.asObservable();
  }

  private price$_ = this.getOrderPrice$();
  public get price$() {
    return this.price$_;
  }

  private loading_ = signal(false);
  public get loading() {
    return this.loading_.asReadonly();
  }

  private calculatingRoute_ = signal(false);
  public get calculatingRoute() {
    return this.calculatingRoute_.asReadonly();
  }

  private showRoutePreview_ = signal(false);
  public get showRoutePreview() {
    return this.showRoutePreview_.asReadonly();
  }

  private activeRoute_ = linkedSignal(() => this.routes().at(0)?.value ?? null);
  public get activeRoute() {
    return this.activeRoute_.asReadonly();
  }

  private restorePoints$_ = new Subject<OrderFormRestorePointType>();
  public get restorePoints$() {
    return this.restorePoints$_.asObservable();
  }

  private uuid = linkedSignal(() => this.order()?.uuid);
  private routes_ = signal<OnyxRoute[]>([]);
  private isRoutePlan = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map((params) => params.has(OrdersQueryParamKey.PLAN_ROUTE)),
    ),
    {
      initialValue: this.activatedRoute.snapshot.queryParamMap.has(
        OrdersQueryParamKey.PLAN_ROUTE,
      ),
    },
  );

  constructor() {
    effect(() => this.routeService.points.set(this.points()));
    effect(() => this.routeService.order.set(this.order()));
    effect(() => this.routeService.isDedicated.set(this.isDedicated()));

    this.routeService.add$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ point, index }) => this.addPoint(point, index));
    this.routeService.move$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ index, direction }) => this.movePoint(index, direction));
    this.routeService.edit$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeRoutePreview());
    this.routeService.remove$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ index }) => this.removePoint(index));
  }

  public getOrderPrice$(): Observable<OrderFormPrice> {
    return this.valueChanges$.pipe(
      map((form): OnyxAmountForm | null => {
        const { basicInformation, points, route } = form;
        const { rate, rateType } = basicInformation;
        const { value, currency } = rate;
        if (value == null || currency == null) return null;

        switch (rateType) {
          case OrderRateType.FREIGHT:
            return { value, currency, date: null };
          case OrderRateType.TONNE:
            return chain(points)
              .filter(
                (point): point is OrderLoadingPointForm =>
                  point['category'] === OrderPointCategory.LOADING,
              )
              .flatMap(({ goods }) => goods)
              .sumBy((good) => good.totalWeight ?? 0)
              .thru((totalWeight) => ({
                value: Math.ceil(value * (totalWeight / 1_000)),
                currency,
                date: null,
              }))
              .value();
          case OrderRateType.EVERY_KILOMETER:
          case OrderRateType.LOADED_KILOMETER: {
            if (!route) return null;
            return {
              value: Math.ceil(value * (route.summary.distance / 1_000)),
              currency,
              date: null,
            };
          }
          case OrderRateType.CUBIC_METER:
            return chain(points)
              .filter(
                (point): point is OrderLoadingPointForm =>
                  point['category'] === OrderPointCategory.LOADING,
              )
              .flatMap(({ goods }) => goods)
              .sumBy((good) => {
                const quantity = good.quantity ?? 0;
                const { size } = good;
                if (size.volume) return (quantity * size.volume) / 10 ** 3;

                const dimensions = size.loadingMeters
                  ? { length: size.loadingMeters, ...LOADING_METERS_DIMENSIONS }
                  : size;
                return chain(dimensions)
                  .thru((size) => [size.length, size.width, size.height])
                  .reduce((result, dimension) => result * (dimension ?? 0), 1)
                  .thru((volume) => (quantity * volume) / 10 ** (3 * 2))
                  .value();
              })
              .thru((totalVolume) => ({
                value: Math.ceil(value * (totalVolume / 100)),
                currency,
                date: null,
              }))
              .value();
          default:
            return null;
        }
      }),
      distinctUntilChanged((previous, current) => isEqual(previous, current)),
      shareReplay(1),
      combineLatestWith(this.sender$),
      switchMap(([price, sender]): Observable<OrderFormPrice> => {
        const tradeCreditLimit = sender?.payment.parameters.tradeCreditLimit;
        if (!price || !sender || tradeCreditLimit == null) {
          return of({ price, senderPrice: null, isOverCreditLimit: false });
        }

        if (tradeCreditLimit.currency === price.currency) {
          return of({
            price,
            senderPrice: price,
            isOverCreditLimit: tradeCreditLimit.value < price.value,
          });
        }

        return this.amountService
          .calculateAmount({
            ...price,
            resultCurrency: tradeCreditLimit.currency,
          })
          .pipe(
            debounceTime(ONYX_INPUT_DEBOUNCE_TIME),
            map((senderPrice) => ({
              price,
              senderPrice,
              isOverCreditLimit: tradeCreditLimit.value < senderPrice.value,
            })),
          );
      }),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public addRestorePoint(restorePoint: OrderFormRestorePointType): void {
    this.restorePoints$_.next(restorePoint);
  }

  public restoreForm(order: PartialDeep<OrderForm>): void {
    FormHelper.reset(this.form);

    const documents = order.documents ?? [];
    const documentsControl = this.form.controls.documents;
    for (const _ of documents) {
      documentsControl.push(DocumentsModalFormComponent.buildForm(this.fb));
    }

    const points = order.points as OrderPointForm[];
    const pointsControl = this.form.controls.points;
    for (const point of points ?? []) {
      switch (point.category) {
        case OrderPointCategory.LOADING:
          pointsControl.push(
            OrderFormHelper.buildLoadingPointForm(
              this.fb,
              point.timeWindows?.windows?.length,
              point.goods?.length,
            ),
          );
          break;
        case OrderPointCategory.UNLOADING:
          pointsControl.push(
            OrderUnloadingPointFormComponent.buildForm(
              this.fb,
              point.timeWindows?.windows?.length,
              point.goods?.length,
            ),
          );
          break;
        case OrderPointCategory.CHECKPOINT:
        case OrderPointCategory.PICKUP:
          pointsControl.push(
            OrderCheckpointFormComponent.buildForm(
              this.fb,
              point.category,
              point.type,
              point?.includeInOrder ?? false,
              false,
            ),
          );
          break;
        case OrderPointCategory.TRANSIT:
          pointsControl.push(
            OrderTransitFormComponent.buildForm(
              this.fb,
              point.type,
              point?.includeInOrder ?? false,
              false,
            ),
          );
          break;
      }
    }
  }

  public changeSender(sender: Contractor | null): void {
    this.sender$_.next(sender);
  }

  public addPoint(
    point: {
      category: OrderPointCategory;
      value: string;
      includeInOrder: boolean;
      isAlwaysOpen: boolean;
    },
    index?: number,
    address?: OnyxCustomAddress | null,
  ): void {
    const pointsForm = this.form.controls.points;
    index ??= pointsForm.length;

    switch (point.category) {
      case OrderPointCategory.LOADING:
        pointsForm.insert(
          index,
          OrderFormHelper.buildLoadingPointForm(this.fb),
        );
        this.closeRoutePreview();
        break;
      case OrderPointCategory.UNLOADING:
        pointsForm.insert(
          index,
          OrderUnloadingPointFormComponent.buildForm(this.fb),
        );
        this.closeRoutePreview();
        break;
      case OrderPointCategory.CHECKPOINT:
      case OrderPointCategory.PICKUP:
        pointsForm.insert(
          index,
          OrderCheckpointFormComponent.buildForm(
            this.fb,
            point.category,
            point.value as OrderPointTypeByCategory[
              OrderPointCategory.CHECKPOINT | OrderPointCategory.PICKUP],
            point.includeInOrder,
            point.isAlwaysOpen,
          ),
        );
        break;
      case OrderPointCategory.TRANSIT:
        pointsForm.insert(
          index,
          OrderTransitFormComponent.buildForm(
            this.fb,
            point.value as OrderPointTypeByCategory[OrderPointCategory.TRANSIT],
            point.includeInOrder,
            point.isAlwaysOpen,
          ),
        );
        break;
    }

    if (address) {
      const pointForm = pointsForm.at(index) as OrderPointFormGroup;
      'address' in pointForm.controls
        ? pointForm.controls.address.setValue(address)
        : pointForm.controls.startAddress.setValue(address);
    }
  }

  public movePoint(index: number, direction: -1 | 1): void {
    const points = this.form.controls.points;
    const hasRelatedGoods = chain([index, index + direction])
      .map((index) => points.at(index).getRawValue() as OrderPointForm)
      .filter((point) => 'goods' in point)
      .flatMap((point) => point.goods)
      .map((good) => good.uuid)
      .thru((goods) => uniq(goods).length !== goods.length)
      .value();

    if (hasRelatedGoods) {
      this.restorePoints$_.next(OrderFormRestorePointType.MOVED_POINT);
      this.closeRoutePreview();
    }

    moveItemInArray(points.controls, index, index + direction);
    points.updateValueAndValidity();
  }

  public removePoint(index: number): void {
    const pointsForm = this.form.controls.points;
    this.addRestorePoint(OrderFormRestorePointType.REMOVED_POINT);
    this.form.controls.points.removeAt(index);
    pointsForm.updateValueAndValidity();
  }

  public setRoutes(
    routes: OnyxRoute[],
    previousRouteSummary?: OnyxRouteSummary,
    routeHandle?: string | null,
  ): void {
    const previousRoute = routeHandle
      ? routes.find(({ handle }) => handle === routeHandle)
      : routes.find(
          ({ summary }) =>
            previousRouteSummary?.distance === summary.distance &&
            previousRouteSummary?.duration === summary.duration,
        );

    this.routes_.set(routes);
    this.changeRoute(previousRoute ?? this.activeRoute());
  }

  public updateRoutes(options?: {
    useRouteHandle: boolean;
  }): Observable<OnyxRoute[]> {
    if (this.hasInvalidAddress()) return EMPTY;

    const form = this.form.getRawValue();
    const routeHandles = form.points.map(
      (point) => point?.['route']?.routeHandle ?? null,
    );
    const routeHandle =
      uniq(routeHandles).length === 1 ? routeHandles[0] : null;
    const previousRouteSummary = form.route?.summary;
    const useRouteHandle = options?.useRouteHandle && routeHandle != null;

    this.setRoutes([]);

    const dto = this.getDto();

    return this.getRouteConfig(dto).pipe(
      tap(() => this.calculatingRoute_.set(true)),
      switchMap((routeConfig) => {
        const body: CalculateRouteRequest = {
          ...routeConfig,
          points: dto.points
            .filter((point): point is OrderPointForm => point != null)
            .map((point) =>
              'address' in point
                ? {
                    category: point.category,
                    coordinates: point.address!.coordinates,
                  }
                : {
                    category: point.category,
                    from: point.startAddress!.coordinates,
                    to: point.endAddress!.coordinates,
                  },
            ),
          routeHandle: useRouteHandle ? routeHandle : null,
        };

        return this.addressService.calculateRoute(body).pipe(
          switchMap((response) =>
            useRouteHandle && !response.routes.length
              ? this.addressService.calculateRoute({
                  ...body,
                  routeHandle: null,
                })
              : of(response),
          ),
        );
      }),
      switchMap(({ routes }) => {
        const activeRouteHandle =
          routeHandle != null &&
          routes.some(({ handle }) => handle === routeHandle)
            ? routeHandle
            : null;

        this.setRoutes(routes, previousRouteSummary, activeRouteHandle);
        this.calculatingRoute_.set(false);

        const route = routes.at(0);
        if (!route) {
          this.toastService.showError({
            message: `${this.I18N}.toasts.noRoute`,
          });
          return EMPTY;
        } else if (!route.summary.distance) {
          this.toastService.showError({
            message: `${this.I18N}.toasts.emptyRoute`,
          });
          return EMPTY;
        }

        return of(routes);
      }),
    );
  }

  public changeRoute(
    route: OnyxRoute | null,
    options?: { isVerified?: boolean; isBlocked?: boolean },
  ): void {
    this.activeRoute_.set(route);
    this.form.controls.route.setValue(
      route
        ? {
            isVerified: options?.isVerified ?? false,
            isBlocked: options?.isBlocked ?? false,
            config: this.routeConfig()!,
            summary: route.summary,
          }
        : null,
    );

    const routeHandle = route?.handle ?? null;
    const pointsForm = this.form.controls.points;
    for (const index of this.form.controls.points.controls.keys()) {
      const pointForm = pointsForm.at(index) as OrderPointFormGroup;
      const routePoint = route?.points[index];
      pointForm.controls.route.setValue(
        routePoint && routeHandle
          ? { ...routePoint, routeHandle }
          : (routePoint ?? null),
      );
    }
  }

  public cancel(force = false): void {
    this.cacheService.delete(OrdersStorageKey.ADD_ORDER_FORM);
    this.returnToOrigin(force);
  }

  public async submit(options: {
    verifyRoute: boolean;
    ignoreCreditLimit: boolean;
    isVerified?: boolean;
    isBlocked?: boolean;
    isDraft?: boolean;
  }): Promise<void> {
    if (this.loading()) return;
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    for (const pointForm of this.form.controls.points.controls) {
      pointForm.setErrors(null);
    }

    const dto = this.getDto();
    if (!this.validatePoints(dto)) return;
    if (!this.validateSemiTrailerPoints(dto)) return;

    const { verifyRoute, ignoreCreditLimit, isDraft = false } = options;

    this.loading_.set(true);
    of(dto)
      .pipe(
        switchMap((dto) => zip(of(dto), this.getRouteConfig(dto))),
        switchMap(([dto, routeConfig]) => {
          if (
            isEqual(dto.route?.config, routeConfig) &&
            (!verifyRoute ? dto.route : this.activeRoute())
          ) {
            return of(null);
          }

          return this.updateRoutes({ useRouteHandle: true });
        }),
        switchMap(() => {
          if (
            !verifyRoute &&
            !this.validatePointsRoute({ showErrors: true }).ok
          ) {
            return EMPTY;
          }

          const continueAction = () => {
            if (verifyRoute) {
              this.showRoutePreview_.set(true);
              return EMPTY;
            }

            const dto = this.getDto();
            dto.route = {
              ...dto.route!,
              isVerified: options.isVerified ?? dto.route?.isVerified ?? false,
              isBlocked: options.isBlocked ?? dto.route?.isBlocked ?? false,
            };

            return this.saveOrder(dto, isDraft);
          };
          if (ignoreCreditLimit || isDraft) return continueAction();

          return this.getOrderPrice$().pipe(
            first(),
            filter(({ isOverCreditLimit }) => !isOverCreditLimit),
            switchMap(() => continueAction()),
          );
        }),
      )
      .subscribe({
        next: (order) => {
          const message = isDraft
            ? 'draftSuccess'
            : `${this.canDispatch() ? OnyxFormMode.ADD : OnyxFormMode.EDIT}Success`;

          this.toastService.showSuccess({
            message: this.translateService.instant(`${this.I18N}.${message}`, {
              identifier: order.identifier,
            }),
            ...(isDraft && {
              description: this.translateService.instant(
                `${this.I18N}.draftSuccessDescription`,
                { identifier: order.identifier },
              ),
            }),
            ...(this.mode() === OnyxFormMode.ADD &&
              !isDraft && {
                description: `${this.I18N}.addSuccessDescription`,
                buttons: [
                  {
                    text: `${this.I18N}.addSimilar`,
                    callback: () =>
                      this.ordersService
                        .getOrder(order.uuid)
                        .subscribe((template) =>
                          this.orderHelper.duplicate(template),
                        ),
                    color: 'black',
                    variant: 'ghost',
                    size: 24,
                  },
                ],
              }),
            keepOnNavigation: true,
          });
          this.cancel();
        },
        error: (response: OnyxHttpErrorResponse) => {
          if (!VALIDATION_ERROR_CODES.includes(response.status)) throw response;

          const { uuid, registrationNumber } =
            response.error?.basicInformation?.loadedSemiTrailer ?? {};
          const { reason, values } = response.error.referenceNumber ?? {};

          if (
            [registrationNumber, uuid].includes(
              'There is another order with same value.',
            )
          ) {
            this.toastService.showError({
              message: `${this.I18N}.semiTrailerIsAssigned`,
              description: `${this.I18N}.semiTrailerIsAssignedDescription`,
            });
            response.handled = true;
          }

          if (reason === 'Reference number should be unique.') {
            this.toastService.showError({
              message: `${this.I18N}.referenceNumberIsUsed`,
              description: this.joinPipe.transform(values, {
                separator: ', ',
                maxLength: 3,
              }),
            });
            response.handled = true;
          }

          throw response;
        },
      })
      .add(() => this.loading_.set(false));
  }

  public closeRoutePreview(): void {
    this.showRoutePreview_.set(false);
  }

  public getGoods(): OrderFormGood[] {
    const pointsForm = this.value().points as OrderPointForm[];
    return pointsForm
      .map((form, index) => ({ form, index }))
      .filter(({ form }) => form['category'] === OrderPointCategory.LOADING)
      .map(({ form, index }) => ({
        loadingPoint: form as OrderLoadingPointForm,
        index,
      }))
      .flatMap(({ loadingPoint, index }) =>
        loadingPoint.goods?.map((good) => ({
          loadingPoint,
          index,
          good,
        })),
      )
      .map(({ loadingPoint, index, good }, goodIndex): OrderFormGood => {
        const unloadingPoints = pointsForm
          .filter(
            (form): form is OrderUnloadingPointForm =>
              form['category'] === OrderPointCategory.UNLOADING,
          )
          .map((unloadingPoint, index) => ({
            unloadingPoint,
            unloadingPointIndex: index,
            goodQuantity: unloadingPoint.goods.find(
              ({ uuid }) => uuid === good.uuid,
            )?.quantity,
          }))
          .filter(({ goodQuantity }) => goodQuantity !== undefined);

        return {
          good,
          goodIndex,
          goodColor:
            ORDER_FORM_GOOD_COLORS[goodIndex % ORDER_FORM_GOOD_COLORS.length],
          loadingPoint,
          pointIndex: index,
          unloadingPoints: new Map(
            unloadingPoints.map((unloadingPoint) => [
              unloadingPoint.unloadingPointIndex,
              unloadingPoint.goodQuantity || 0,
            ]),
          ),
          availableQuantity:
            (OrderHelper.getGoodQuantity(good) ?? 0) -
            sumBy(unloadingPoints, ({ goodQuantity }) => goodQuantity ?? 0),
        };
      });
  }

  private saveOrder(
    dto: OrderFormDto,
    isDraft: boolean,
  ): Observable<OrderReference> {
    if (isDraft) dto = { ...dto, dispatch: OrderDispatch.DRAFT };

    if (this.mode() === OnyxFormMode.ADD) {
      return this.ordersService
        .addOrder(dto)
        .pipe(tap((order) => this.uuid.set(order.uuid)));
    }

    return this.ordersService.editOrder(this.uuid()!, dto);
  }

  private getDto(): OrderFormDto {
    const form = this.form.getRawValue();
    const {
      loadedSemiTrailer,
      isDropped,
      pickupPointUuid,
      dropPointUuid,
      ...basicInformation
    } = form.basicInformation;

    const dto: OrderFormDto = {
      ...form,
      parameters: ParametersFormComponent.getDto(
        form.parameters,
        this.validation()!['additionalParameters'],
      ),
      basicInformation: {
        ...basicInformation,
        loadedSemiTrailer: {
          uuid:
            loadedSemiTrailer && validateUuid(loadedSemiTrailer)
              ? loadedSemiTrailer
              : null,
          registrationNumber:
            loadedSemiTrailer && !validateUuid(loadedSemiTrailer)
              ? loadedSemiTrailer
              : null,
          isDropped,
          pickupPointUuid: loadedSemiTrailer ? pickupPointUuid : null,
          dropPointUuid: isDropped ? dropPointUuid : null,
        },
      },
      points: form.points.map((point) => {
        if (point['category'] !== OrderPointCategory.LOADING) return point;

        const loadingPoint = point as OrderLoadingPointForm;
        return {
          ...loadingPoint,
          goods: loadingPoint.goods.map((good) => ({
            ...good,
            type: good.type && validateUuid(good.type) ? good.type : null,
            typeName: good.type && !validateUuid(good.type) ? good.type : null,
            size: good.size.type && good.size,
          })),
        };
      }),
    };

    return dto;
  }

  private validatePoints(dto: OrderFormDto): boolean {
    try {
      const pointsCategoryCount = chain(dto.points)
        .countBy('category')
        .value() as Record<OrderPointCategory, number>;
      if (
        !pointsCategoryCount[OrderPointCategory.LOADING] ||
        !pointsCategoryCount[OrderPointCategory.UNLOADING]
      ) {
        throw `${this.I18N}.toasts.noLoadingOrUnloading`;
      }

      const isDedicated = this.isDedicated();
      const loadingUnloadingCount =
        (pointsCategoryCount[OrderPointCategory.LOADING] ?? 0) +
        (pointsCategoryCount[OrderPointCategory.UNLOADING] ?? 0);
      if (!isDedicated && loadingUnloadingCount > 2) {
        throw this.translateService.instant(
          `${this.I18N}.toasts.ltlPointsLimit`,
          { limit: 2, overflow: loadingUnloadingCount - 2 },
        );
      }

      const hasSize = this.getGoods().some((good) => !!good.good.size.type);
      const hasNoSize = this.getGoods().some((good) => !good.good.size.type);
      if (isDedicated && hasSize && hasNoSize) {
        throw this.translateService.instant(
          `${this.I18N}.toasts.invalidGoodSizes`,
        );
      }

      if (!isDedicated) {
        const illegalPoints = chain(dto.points as OrderPointForm[])
          .map((point) => point.type)
          .filter((type) => !LTL_ORDER_POINT_TYPES.includes(type))
          .uniq()
          .map((type) =>
            this.translateService.instant(
              `${DictionaryCode.ORDER_POINT_TYPE}.${type}`,
            ),
          )
          .join(', ')
          .value();
        if (illegalPoints) {
          throw this.translateService.instant(
            `${this.I18N}.toasts.ltlIllegalPoints`,
            { illegalPoints },
          );
        }
      }

      const notUnloadedGoods$ = chain(this.getGoods())
        .filter((good) => good.availableQuantity > 0)
        .map((good) => good.good.type!)
        .map((goodType) => {
          if (!validateUuid(goodType)) return of(`"${goodType}"`);
          return this.goodTypes$.pipe(
            first(),
            map((goodTypes) => goodTypes.find(({ uuid }) => uuid === goodType)),
            map((type) =>
              type ? this.languagePipe.transform(type.names) : goodType,
            ),
            map((type) => `"${type}"`),
          );
        })
        .value();
      if (notUnloadedGoods$.length) {
        throw forkJoin(notUnloadedGoods$).pipe(
          map((notUnloadedGoods) => {
            if (notUnloadedGoods.length === 1) {
              return this.translateService.instant(
                `${this.I18N}.toasts.notUnloadedGood`,
                { good: notUnloadedGoods[0] },
              );
            }

            return this.translateService.instant(
              `${this.I18N}.toasts.notUnloadedGoods`,
              { goods: notUnloadedGoods.join(', ') },
            );
          }),
          tap((error) => this.toastService.showError({ message: error })),
        );
      }
    } catch (error) {
      if (isString(error)) this.toastService.showError({ message: error });
      if (isObservable(error)) error.subscribe();
      return false;
    }

    return true;
  }

  private validateSemiTrailerPoints(dto: OrderFormDto): boolean {
    const { pickupPointUuid, dropPointUuid } =
      dto.basicInformation.loadedSemiTrailer;

    const showError = (key: 'invalidPickupPoint' | 'invalidDropPoint') => {
      this.toastService.showError({
        message: `${this.I18N}.toasts.${key}`,
      });
      return false;
    };

    const getIndex = (uuid: string | null) =>
      uuid ? this.points().findIndex((point) => point.value.uuid === uuid) : -1;

    const pickupPointIndex = getIndex(pickupPointUuid);
    const dropPointIndex = getIndex(dropPointUuid);

    if (pickupPointUuid && pickupPointIndex > this.firstLoadingPoint()!.index) {
      return showError('invalidPickupPoint');
    }

    if (dropPointUuid && dropPointIndex < this.lastUnloadingPoint()!.index) {
      return showError('invalidDropPoint');
    }

    return true;
  }

  private validatePointsRoute(options: { showErrors: boolean }): {
    ok: boolean;
    routeCompliance: OnyxScheduleCompliance;
    pointsCompliance: OnyxScheduleCompliance[];
  } {
    const getNextDate = (
      previous: DateTime | null,
      window: { from: DateTime; to: DateTime } | null,
    ): { compliance: OnyxScheduleCompliance; next: DateTime | null } => {
      if (window == null || previous == null) {
        return {
          compliance: OnyxScheduleCompliance.ON_TIME,
          next: previous ?? window?.from ?? null,
        };
      }

      const next = DateTime.max(previous, window.from);
      const timeBuffer = window.to.diff(previous);

      if (timeBuffer.toMillis() < 0) {
        return { compliance: OnyxScheduleCompliance.DELAYED, next };
      } else if (timeBuffer < SCHEDULE_COMPLIANCE_DELAY_RISK_THRESHOLD) {
        return { compliance: OnyxScheduleCompliance.DELAY_RISK, next };
      }
      return { compliance: OnyxScheduleCompliance.ON_TIME, next };
    };

    const pointsCompliance = [];
    let lastDate: DateTime | null = null;

    const pointsControl = this.form.controls.points;
    for (const pointForm of pointsControl.controls) {
      const point = pointForm.getRawValue() as OrderPointForm;
      const { category, serviceTime } = point;

      if (lastDate) {
        lastDate = lastDate.plus({
          seconds: point.route?.summary.duration ?? 0,
        });
      }

      let result: ReturnType<typeof getNextDate>;
      if (
        category === OrderPointCategory.LOADING ||
        category === OrderPointCategory.UNLOADING
      ) {
        result = chain(point.timeWindows.windows)
          .map((window) => ({
            date: window.date!,
            timeFrom: window.timeRange?.from ?? window.time!,
            timeTo: window.timeRange?.to ?? window.time!,
          }))
          .map((window) => ({
            from: DateTime.fromISO(`${window.date}T${window.timeFrom}`),
            to: DateTime.fromISO(`${window.date}T${window.timeTo}`),
          }))
          .orderBy((window) => window.from)
          .map((window) => getNextDate(lastDate, window))
          .thru(
            (results) =>
              results.find(
                ({ compliance }) =>
                  compliance !== OnyxScheduleCompliance.DELAYED,
              ) ?? results[0],
          )
          .value();
      } else if (
        category === OrderPointCategory.CHECKPOINT ||
        category === OrderPointCategory.PICKUP
      ) {
        if (lastDate == null) {
          result = { compliance: OnyxScheduleCompliance.ON_TIME, next: null };
        } else {
          result = chain(lastDate)
            .thru((lastDate) =>
              Array.from({ length: 4 }).map((_, index) =>
                lastDate.plus({ days: index }),
              ),
            )
            .map((date) => {
              const openingHours = CommonHelper.getOpeningHours(
                date,
                point.businessHours as BusinessHours,
              );
              if (!openingHours) return null;

              const timeFrom = DateTime.fromISO(openingHours.from);
              const timeTo = DateTime.fromISO(openingHours.to);

              const from = date.set({
                hour: timeFrom.hour,
                minute: timeFrom.minute,
              });
              const to = date.set({
                hour: timeTo.hour,
                minute: timeTo.minute,
              });

              return { from, to };
            })
            .map((window) => getNextDate(lastDate, window))
            .thru(
              (results) =>
                results.find(({ compliance: ok }) => ok) ?? results[0],
            )
            .value();
        }
      } else if (category === OrderPointCategory.TRANSIT) {
        const [type, date, time] = [
          point.timeWindow.type,
          point.timeWindow.date!,
          point.timeWindow.time!,
        ];

        if (type === OrderTransitPointTimeWindowType.FIX) {
          const dateTime = DateTime.fromISO(`${date}T${time}`);
          const window = { from: dateTime, to: dateTime };
          result = getNextDate(lastDate, window);
        } else {
          result = {
            compliance: OnyxScheduleCompliance.ON_TIME,
            next: lastDate,
          };
        }
      } else {
        result = { compliance: OnyxScheduleCompliance.DELAYED, next: lastDate };
      }

      lastDate = result.next;
      if (lastDate && serviceTime) {
        lastDate = lastDate.plus({ seconds: serviceTime });
      }

      pointsCompliance.push(result.compliance);
      if (
        result.compliance === OnyxScheduleCompliance.DELAYED &&
        options.showErrors
      ) {
        pointForm.setErrors({ invalidTimeWindow: true });
      }
    }

    let routeCompliance = OnyxScheduleCompliance.ON_TIME;
    if (pointsCompliance.includes(OnyxScheduleCompliance.DELAYED)) {
      routeCompliance = OnyxScheduleCompliance.DELAYED;
    } else if (pointsCompliance.includes(OnyxScheduleCompliance.DELAY_RISK)) {
      routeCompliance = OnyxScheduleCompliance.DELAY_RISK;
    }

    const ok = routeCompliance !== OnyxScheduleCompliance.DELAYED;
    if (!ok && options.showErrors) {
      this.toastService.showError({
        message: `${this.I18N}.toasts.invalidPointsTimeWindows`,
      });
    }

    return { ok, routeCompliance, pointsCompliance };
  }

  private getRouteConfig(dto: OrderFormDto): Observable<RouteConfig> {
    const routeConfig = this.routeConfig();
    const { vehicleTypes, rate } = dto.basicInformation;

    if (routeConfig) {
      routeConfig.resultCurrency = rate.currency!;
      this.routeConfig.set(routeConfig);

      return of(routeConfig);
    }

    return OrderFormRouteConfigModalComponent.getDefaultConfig(this.injector, {
      vehicleTypes,
      resultCurrency: rate.currency!,
    }).pipe(tap((routeConfig) => this.routeConfig.set(routeConfig)));
  }

  private buildForm(): OrderFormGroup {
    return OrderFormHelper.buildForm(
      this.fb,
      this.authStore,
      this.preferencesService,
      this.orderFormHelper.getDefaultDispatch(),
    );
  }
}
